import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { PageHeader } from '@/components/PageHeader';
import { PhotoGrid } from '@/components/PhotoGrid';
import { QueryState } from '@/components/QueryState';
import { shortDate, today } from '@/lib/format';
import { keys, useDailyLogActions, useDailyLogs } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { DailyLog } from '@/lib/types';
import { DailyLogEditor } from '@/pages/daily/DailyLogEditor';
import { SubmitPreviewModal } from '@/pages/daily/SubmitPreviewModal';

/**
 * 작업일보.
 *
 * <p>일보는 현장당 하루 한 장이고(`uq_pmis_daily_logs_site_date`), 제출하면 공정율 스냅샷이 다시
 * 계산된다(`DailyLogProgressService`). 그 버튼을 협력사가 쥐면 <b>현장 공정율이 협력사 손에 들어간다</b> —
 * 그래서 협력사는 실적 줄과 사진만 올리고, 작성·저장·제출은 <b>직원</b>이 포털에서 한다
 * (DOC-82 §4-3 · §10 ⑦ · DOC-87 결정 ⑥). 감리는 읽는다.
 *
 * <p>사진은 증빙을 만드는 손(협력사·직원)이 <b>작성 중</b>인 일보에만 붙인다(DOC-84 §3-D-4 · DOC-87 §3-4).
 * 제출된 일보는 공정율의 근거라 닫혀 있다. 사진이 없고 붙일 수도 없는 일보에는 칸을 세우지 않는다.
 */
export function DailyLogPage() {
  const { siteId, siteName, partyType, isStaff, canSubmitEvidence } = useSite();
  const queryClient = useQueryClient();
  const logs = useDailyLogs(siteId);
  const { unsubmit } = useDailyLogActions(siteId);
  const rows = logs.data ?? [];

  /** 편집 중인 일보. `'new'` 는 새 장, 아니면 고치는 장의 id. */
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [submitting, setSubmitting] = useState<DailyLog | null>(null);
  /** 마지막 제출에서 수기 값과 부딪혀 반영하지 않은 공정 — 조용히 넘어가면 사람은 반영된 줄 안다. */
  const [skipped, setSkipped] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);

  const editingRow = editing === null || editing === 'new' ? null : rows.find((it) => it.id === editing) ?? null;

  /** 오늘 것이 이미 있으면 그 장을 연다 — 하루 한 장이라 새로 만들면 서버가 거절한다. */
  function onWrite() {
    const todays = rows.find((it) => it.logDate === today());
    setEditing(todays !== undefined ? todays.id : 'new');
  }

  async function onUnsubmit(id: string) {
    setFailed(false);
    try {
      await unsubmit.mutateAsync(id);
    }
    catch {
      setFailed(true);
    }
  }

  return (
    <>
      <PageHeader
        title={partyType === 'SUPERVISOR' ? '작업일보 조회' : '작업일보'}
        subtitle={siteName}
        action={
          isStaff && editing === null && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={onWrite}>
              작성
            </button>
          )
        }
      />

      {editing !== null && (
        <DailyLogEditor
          key={editing}
          siteId={siteId}
          initial={editingRow}
          onSaved={() => setEditing(null)}
          onCancel={() => setEditing(null)}
        />
      )}

      {failed && (
        <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>저장하지 못했습니다.</p>
      )}

      {skipped.length > 0 && (
        <p className="mb-3 rounded-md p-3 text-ui-xs" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}>
          수기 진행율과 달라 반영하지 않은 공정: {skipped.join(', ')}
        </p>
      )}

      <QueryState
        pending={logs.isPending}
        error={logs.isError}
        empty={rows.length === 0}
        emptyText="작성된 일보가 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="card">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-ui-md font-medium">{shortDate(row.logDate)}</div>
                  <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                    {[row.weather, row.temperature].filter(Boolean).join(' · ') || '날씨 미기재'}
                    {row.manpowerCount !== null && ` · 총 ${row.manpowerCount}명`}
                    {row.equipmentSummary !== null && ` · ${row.equipmentSummary}`}
                  </div>
                </div>
                <span className={`badge ${row.status === 'SUBMITTED' ? 'badge-gray' : 'badge-blue'}`}>
                  {row.status === 'SUBMITTED' ? '제출됨' : '작성 중'}
                </span>
              </div>

              {/* 패드 가로에서 금일·명일이 나란히 — 적을 때와 같은 자리에서 읽는다. */}
              {(row.todayWork !== null || row.tomorrowPlan !== null) && (
                <div className="mb-2 grid gap-2 md:grid-cols-2">
                  {row.todayWork !== null && (
                    <div>
                      <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>금일</div>
                      <p className="text-ui-sm">{row.todayWork}</p>
                    </div>
                  )}
                  {row.tomorrowPlan !== null && (
                    <div>
                      <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>명일</div>
                      <p className="text-ui-sm">{row.tomorrowPlan}</p>
                    </div>
                  )}
                </div>
              )}

              {row.progressNote !== null && (
                <p className="mb-2 text-ui-xs" style={{ color: 'var(--text-secondary)' }}>{row.progressNote}</p>
              )}

              {row.items.length > 0 && (
                <ul className="flex flex-col gap-1 border-t pt-2 text-ui-xs" style={{ borderColor: 'var(--border)' }}>
                  {row.items.map((item) => (
                    <li key={item.id} className="flex justify-between gap-3">
                      <span className="min-w-0 truncate" style={{ color: 'var(--text-secondary)' }}>
                        {item.wbsItemName}
                      </span>
                      <span className="shrink-0">
                        {item.quantity !== null
                          ? `${item.quantity.toLocaleString('ko-KR')}${item.unit ?? ''}`
                          : `${item.progressRate ?? 0}%`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {(row.photoCount > 0 || (canSubmitEvidence && row.status === 'DRAFT')) && (
                <div className="mt-2 border-t pt-2" style={{ borderColor: 'var(--border)' }}>
                  <PhotoGrid
                    target="daily-logs"
                    parentId={row.id}
                    canUpload={canSubmitEvidence && row.status === 'DRAFT'}
                    onUploaded={() => void queryClient.invalidateQueries({ queryKey: keys.dailyLogs(siteId) })}
                  />
                </div>
              )}

              {/* ── 직원: 수정·제출·제출 취소 ─────────────────────────────────── */}
              {isStaff && row.status === 'DRAFT' && editing === null && (
                <div className="mt-3 flex gap-2">
                  <button type="button" className="btn-secondary flex-1" onClick={() => setEditing(row.id)}>
                    수정
                  </button>
                  <button type="button" className="btn-primary flex-1" onClick={() => { setSkipped([]); setSubmitting(row); }}>
                    제출
                  </button>
                </div>
              )}

              {isStaff && row.status === 'SUBMITTED' && (
                <button
                  type="button"
                  className="btn-secondary mt-3 w-full"
                  onClick={() => onUnsubmit(row.id)}
                  disabled={unsubmit.isPending}
                >
                  제출 취소
                </button>
              )}
            </li>
          ))}
        </ul>
      </QueryState>

      {submitting !== null && (
        <SubmitPreviewModal
          siteId={siteId}
          log={submitting}
          onSubmitted={(result) => {
            setSkipped(result);
            setSubmitting(null);
          }}
          onClose={() => setSubmitting(null)}
        />
      )}
    </>
  );
}
